import { symbolLookup } from "../../../../TIPro/js/symbolLookup.js";
import { FormatHelpers } from "../helpers/format-helpers";
import { TraceLoggingHelpers } from "../helpers/trace-logging-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { CompanyProfileDataModel } from "../models/company-profile/company-profile-data.model.js";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { CustomEventTypes } from "../models/enum/custom-event-types.enum";
import { Timeframe } from "../models/enum/timeframe.enum";
import { ChangeFromCloseValueResult, SingleStockWindowChangeFromCloseManager } from "../models/single-stock-window/single-stock-window-change-from-close.manager";
import { SingleStockWindowComponent } from "../models/single-stock-window/single-stock-window-component.model";
import { SingleStockWindowDataModel } from "../models/single-stock-window/single-stock-window-data.model";
import { WebChartDataService } from "../services/web-chart-data.service";
import { HistoricalChartButtonComponent } from "./historical-chart-button.component";
import { ColoredLastPriceComponent, ColoredLastPriceSource } from "./colored-last-price.component";
import { MarketingAISignalComponent } from "./marketing-ai-signal.component";

declare const $: any;
declare const myCommon: any;

export class MarketingWebChartSingleStockWindowComponent extends SingleStockWindowComponent {
    private stockSectionElement: HTMLElement | null = null;
    private expandBtn: HTMLElement | null = null;

    private iconWrapper: HTMLElement | null = null;
    private symbolSwitcher: HTMLInputElement | null = null;
    private coloredLastPrice: ColoredLastPriceComponent | null = null;
    private changeFromCloseElement: HTMLElement | null = null;
    private countryFlagElement: HTMLImageElement | null = null;
    private changeFromCloseFirstBarElement: HTMLElement | null = null;
    private volElement: HTMLElement | null = null;
    private relVolElement: HTMLElement | null = null;

    private cardHeaderElement: HTMLElement | null = null;
    private cardBodyElement: HTMLElement | null = null;
    private companyNameElement: HTMLElement | null = null;
    private headerContainer: HTMLElement | null = null;
    private companyLogoElement: HTMLElement | null = null;
    private companyLogoErrorElement: HTMLElement | null = null;

    private symbol = '';
    private chartStyle: ChartStyleSettingsModel | null = null;
    private singleStockData: SingleStockWindowDataModel | null = null;

    private chartSymbolLookup: any = null;
    private focusOnTextbox = false;
    private requestingSymbol = false;
    private seperatorColor = '';

    private defaultUpColor = 'rgb(0, 255, 0)';
    private defaultDownColor = 'rgb(255, 0, 0)';

    private readonly symbolLogoUrl = `https://static.trade-ideas.com/CompanyLogos/{0}.png`;
    private readonly symbolCountryUrl = `https://static.trade-ideas.com/Flags/{0}.png`;

    private extraSmallMode = false;

    private singleStockWindowChangeFromCloseManager: SingleStockWindowChangeFromCloseManager | null = null;
    private marketingAISignalComponent: MarketingAISignalComponent | null = null;
    private bannerLayoutApplied = false;
    private savedCompanyNameElement: HTMLElement | null = null;
    private savedCompanyLogoElement: HTMLElement | null = null;
    private savedCompanyLogoErrorElement: HTMLElement | null = null;

    constructor(el: HTMLElement, base: IChartContainer) {
        super(el, base);
        this.singleStockWindowChangeFromCloseManager = this.base.getComponentsByType(SingleStockWindowChangeFromCloseManager)?.[0] as SingleStockWindowChangeFromCloseManager;

        this.stockSectionElement = this.element.querySelector('.stock-section');
        this.expandBtn = base.element.querySelector('.single-stock-expand-btn');

        this.iconWrapper = this.element.querySelector('.icon-wrapper') as HTMLInputElement | null;
        this.symbolSwitcher = this.element.querySelector('.chart-symbol') as HTMLInputElement | null;
        this.coloredLastPrice = new ColoredLastPriceComponent(this.element.querySelector('.last-price'), ColoredLastPriceSource.Chart, this.base);
        this.changeFromCloseElement = this.element.querySelector('.pnl');
        this.countryFlagElement = this.element.querySelector('img.country') as HTMLImageElement | null;
        this.volElement = this.element.findFirst('.vol');
        this.relVolElement = this.element.findFirst('.rel-vol');
        this.changeFromCloseFirstBarElement = this.element.querySelector('.change-from-first-bar');

        if (this.base.isExtraSmall()) {
            this.extraSmallMode = true;
            this.createCompanyLogoCartForPopupChart();
        } else {
            const cardElement = base.element.closest('.card');
            this.cardHeaderElement = cardElement?.querySelector('.card-header');
            this.cardBodyElement = cardElement?.querySelector('.card-body');

            this.createCompanyLogoCard();
        }

        this.subscribeChartDataService();
        this.onElementResize();

        this.onCountryImgLoaded();
        this.onExpandClick();
        this.checkIfJQueryExist(0);
        this.subscribeChartRedraw();
        this.onChartLoaded();
    }

    public getSymbolLookupElement(): HTMLInputElement | null {
        return this.symbolSwitcher;
    }

    private setContentSizeTimer: any;
    public setContentSize() {
        this.stockSectionElement?.classList.remove('five');
        this.stockSectionElement?.classList.remove('four');
        this.stockSectionElement?.classList.remove('three');
        this.stockSectionElement?.classList.remove('two');
        this.stockSectionElement?.classList.remove('small');

        this.element.classList.remove('small');
        this.element.classList.remove('small2x');
        this.element.classList.remove('small3x');

        const isFullScreen = this.element.closest('[data-resize-function="chart-resize"]')?.clientWidth == document.body.clientWidth;

        TraceLoggingHelpers.log('setContentSize isFullScreen ' + isFullScreen + ' ' + this.element.clientWidth)
        if (isFullScreen) {
            this.element.classList.add('fullscreen');
            if (this.element.clientWidth < 460) {
                this.stockSectionElement?.classList.add('two');
                this.stockSectionElement?.classList.add('small');
                this.element.classList.add('small');
                this.element.classList.add('small2x');
                this.element.classList.add('small3x');
                this.setHeaderContainerTextSize('small3x');
            } else if (this.element.clientWidth < 490) {
                this.stockSectionElement?.classList.add('two');
                this.stockSectionElement?.classList.add('small');
                this.element.classList.add('small');
                this.element.classList.add('small2x');
                this.setHeaderContainerTextSize('small2x');
            } else if (this.element.clientWidth < 560) {
                this.stockSectionElement?.classList.add('two');
                this.element.classList.add('small');
                this.element.classList.add('small2x');
                this.setHeaderContainerTextSize('small2x');
            } else if (this.element.clientWidth < 768) {
                this.stockSectionElement?.classList.add('two');
                this.element.classList.add('small');
                this.setHeaderContainerTextSize('small');
            } else if (this.element.clientWidth < 840) {
                this.stockSectionElement?.classList.add('three');
                this.setHeaderContainerTextSize('');
            } else if (this.element.clientWidth < 940) {
                this.stockSectionElement?.classList.add('four');
                this.setHeaderContainerTextSize('');
            } else {
                this.stockSectionElement?.classList.add('five');
                this.setHeaderContainerTextSize('');
            }
        } else {
            this.element.classList.remove('fullscreen');
            if (this.element.clientWidth < 420) {
                this.stockSectionElement?.classList.add('two');
                this.stockSectionElement?.classList.add('small');
                this.element.classList.add('small');
                this.element.classList.add('small2x');
                this.element.classList.add('small3x');
                this.setHeaderContainerTextSize('small3x');
            } else if (this.element.clientWidth < 510) {
                this.stockSectionElement?.classList.add('two');
                this.element.classList.add('small');
                this.element.classList.add('small2x');
                this.setHeaderContainerTextSize('small2x');
            } else if (this.element.clientWidth < 560) {
                this.stockSectionElement?.classList.add('two');
                this.element.classList.add('small');
                this.setHeaderContainerTextSize('small');
            } else if (this.element.clientWidth < 565) {
                this.stockSectionElement?.classList.add('three');
                this.element.classList.add('small');
                this.setHeaderContainerTextSize('small');
            } else if (this.element.clientWidth < 768) {
                this.stockSectionElement?.classList.add('three');
                this.setHeaderContainerTextSize('');
            } else if (this.element.clientWidth < 920) {
                this.stockSectionElement?.classList.add('four');
                this.setHeaderContainerTextSize('');
            } else {
                this.stockSectionElement?.classList.add('five');
                this.setHeaderContainerTextSize('');
            }
        }

        this.updateSingleStockWindow();

        if (!this.element.classList.contains('active')) {
            const elementMinHeight = this.iconWrapper?.offsetHeight + 5;
            this.element.style.height = elementMinHeight + 'px';
            this.element.style.overflow = 'hidden';
        } else {
            this.element.style.height = 'auto';
            this.element.style.overflow = '';
        }

        clearTimeout(this.setContentSizeTimer);
        this.setContentSizeTimer = setTimeout(() => {
            const base = this.base as any;
            base?.pnpContainer?.setPnPSize();
            this.setChartPosition(this.element.clientHeight);
        }, 500);
    }

    private setHeaderContainerTextSize(size: string) {
        if (size == 'small3x') {
            this.companyNameElement.style.fontSize = `12px`;
        } else if (size == 'small2x') {
            this.companyNameElement.style.fontSize = `12px`;
        } else {
            this.companyNameElement.style.fontSize = ``;
        }
    }

    private chartRedrawTimer: any;
    private subscribeChartRedraw() {
        this.base.tiChartSeries.subscribeExtremeChange(this.id, () => {
            clearTimeout(this.chartRedrawTimer);
            this.chartRedrawTimer = setTimeout(() => {
                this.handleChangeFromClose();
            }, 150);
        });

        this.base.tiChartSeries.subscribeRedrawChange(this.id, () => {
            clearTimeout(this.chartRedrawTimer);
            this.chartRedrawTimer = setTimeout(() => {
                this.handleChangeFromClose();
            }, 150);
        });
    }

    private createCompanyLogoCartForPopupChart() {

        const companyName = document.createElement('div');
        companyName.className = 'extra-small-company-name';

        const companyNamePTag = document.createElement('p');
        this.companyNameElement = companyNamePTag;

        companyName.insertAdjacentElement('beforeend', companyNamePTag);
        this.base.chartWrapper.insertAdjacentElement('beforeend', companyName);

        const logoBox = this.element.querySelector<HTMLElement>('.logo-box');
        if (logoBox) {
            logoBox.setAttribute('style', '');
            const imgTag = logoBox.querySelector<HTMLElement>('img');
            const imgErrorTag = logoBox.querySelector<HTMLElement>('.fail-company-logo-box');

            imgTag && (this.companyLogoElement = imgTag);
            imgErrorTag && (this.companyLogoErrorElement = imgErrorTag);
        }

        this.changeFromCloseFirstBarElement?.setAttribute('style', '')
        this.symbolSwitcher.setAttribute('readonly', 'readonly');
        this.onImageLoaded();
    }

    private createCompanyLogoCard() {
        if (!this.cardHeaderElement) {
            return;
        }

        this.companyNameElement = this.cardHeaderElement.querySelector('.card-title');
        if (this.companyNameElement) {
            this.companyNameElement.remove();
        }

        const companyNameElem = document.createElement('h5');
        companyNameElem.className = 'card-title mb-0';

        this.companyNameElement = companyNameElem;
        this.companyNameElement.style.display = 'inline-block';
        this.companyNameElement.style.paddingTop = `10px`;
        this.companyNameElement.style.paddingBottom = `10px`;
        this.companyNameElement.style.minHeight = `38px`;
        this.companyNameElement.style.whiteSpace = `nowrap`;
        this.companyNameElement.style.overflow = `hidden`;
        this.companyNameElement.style.textOverflow = `ellipsis`;

        this.companyNameElement.style.maxWidth = `calc(100% - 64px)`;

        this.companyNameElement.innerHTML = '&nbsp;';

        const headerContainer = document.createElement('div');
        headerContainer.style.marginLeft = '56px';
        headerContainer.style.width = 'calc(100% - 100px)';
        headerContainer.style.display = 'flex';
        headerContainer.style.alignItems = 'center';
        headerContainer.style.position = 'absolute';
        headerContainer.className = 'tichart-header-companyinfo';
        headerContainer.insertAdjacentElement('beforeend', this.companyNameElement);

        this.headerContainer = headerContainer;

        this.cardHeaderElement.insertAdjacentElement('beforeend', this.headerContainer);
        this.cardHeaderElement.setAttribute('style', 'padding-bottom: 22px !important');

        const cartActions = this.cardHeaderElement.querySelector<HTMLElement>('.card-actions');
        if (cartActions) {
            cartActions.style.position = 'relative';
            cartActions.style.zIndex = '3';
        }

        const companyLogoBox = document.createElement('div');
        companyLogoBox.style.position = 'absolute';
        companyLogoBox.style.left = '-52px';
        companyLogoBox.style.top = '0';
        companyLogoBox.className = 'tichart-header-companyinfo company-logo-box';

        const companyLogoImg = document.createElement('img');
        companyLogoImg.style.display = 'none';
        companyLogoImg.style.width = '35px';
        companyLogoImg.style.height = '35px';
        companyLogoImg.style.borderRadius = '10px';

        this.companyLogoElement = companyLogoImg;

        const companyLogoError = document.createElement('div');
        companyLogoError.className = 'company-logo-error';
        companyLogoError.style.display = 'none';
        companyLogoError.style.width = '35px';
        companyLogoError.style.height = '35px';
        companyLogoError.style.borderRadius = '10px';

        this.companyLogoErrorElement = companyLogoError;

        companyLogoBox.insertAdjacentElement('beforeend', companyLogoImg);
        companyLogoBox.insertAdjacentElement('beforeend', companyLogoError);

        headerContainer.insertAdjacentElement('beforeend', companyLogoBox);

        if (this.cardBodyElement) {
            this.cardBodyElement.style.paddingTop = '0px';
        }

        this.onImageLoaded();
    }

    private getMarketingAISignal(): MarketingAISignalComponent | null {
        if (!this.marketingAISignalComponent) {
            this.marketingAISignalComponent = this.base.getComponentsByType(MarketingAISignalComponent)?.[0] as MarketingAISignalComponent ?? null;
            if (this.marketingAISignalComponent) {
                this.marketingAISignalComponent.onChange(() => {
                    this.teardownBannerAlignedLayout();
                    this.chartHeight = null;
                    this.setChartPosition(this.element.clientHeight);
                });
            }
        }
        return this.marketingAISignalComponent;
    }

    // Aligns companyNameElement and companyLogoElement to the simple DOM elements
    // (same approach as tichart-single-stock-window) when banner mode is active.
    // Layout/sizing CSS is handled by the .ai-signal-banner rules in _marketing-ai-signal.scss.
    private setupBannerAlignedLayout() {
        if (this.bannerLayoutApplied) return;
        this.bannerLayoutApplied = true;

        // Save originals so teardown can restore them exactly
        this.savedCompanyNameElement = this.companyNameElement;
        this.savedCompanyLogoElement = this.companyLogoElement;
        this.savedCompanyLogoErrorElement = this.companyLogoErrorElement;

        // Hide card-header company info created by createCompanyLogoCard() — banner mode shows
        // the inline elements instead.
        if (this.headerContainer) this.headerContainer.style.display = 'none';

        // Company name: point to existing .company-name p (same as tichart-single-stock-window)
        const companyNameWrapper = this.element.querySelector<HTMLElement>('.company-name');
        if (companyNameWrapper) {
            companyNameWrapper.style.display = '';  // clear inline display:none
            this.companyNameElement = companyNameWrapper.querySelector('p');
        }

        // Logo: point to existing .logo-box elements, clear inline display:none.
        // Use LOCAL captures in event handlers so they always operate on these specific
        // elements — if the events fire after teardown, they won't contaminate the card logo.
        const logoBox = this.element.querySelector<HTMLElement>('.logo-box');
        if (logoBox) {
            logoBox.setAttribute('style', '');  // clear inline display:none !important
            const imgTag = logoBox.querySelector<HTMLImageElement>('img');
            const imgErrorTag = logoBox.querySelector<HTMLElement>('.fail-company-logo-box');
            if (imgTag) this.companyLogoElement = imgTag;
            if (imgErrorTag) this.companyLogoErrorElement = imgErrorTag;

            if (imgTag && imgErrorTag) {
                imgTag.addEventListener('load', () => {
                    imgTag.style.display = 'block';
                    imgErrorTag.style.display = 'none';
                });
                imgTag.addEventListener('error', () => {
                    const bgColor = typeof myCommon !== 'undefined' ? (myCommon?.colorFromSymbol(this.symbol) ?? '') : '';
                    if (bgColor) imgErrorTag.style.backgroundColor = bgColor;
                    imgTag.style.display = 'none';
                    imgErrorTag.style.display = 'block';
                });
            }

            if (this.symbol) this.updateSymbol(this.symbol);  // set src to trigger load/error
        }
    }

    // Reverts setupBannerAlignedLayout — restores original element references and hides banner elements.
    private teardownBannerAlignedLayout() {
        if (!this.bannerLayoutApplied) return;
        this.bannerLayoutApplied = false;

        // Hide the banner-mode inline elements
        const companyNameWrapper = this.element.querySelector<HTMLElement>('.company-name');
        if (companyNameWrapper) companyNameWrapper.style.display = 'none';

        const logoBox = this.element.querySelector<HTMLElement>('.logo-box');
        if (logoBox) {
            const templateImg = logoBox.querySelector<HTMLImageElement>('img');
            if (templateImg) templateImg.src = '';
            logoBox.style.display = 'none';
            if (this.companyLogoElement) this.companyLogoElement.style.display = 'none';
            if (this.companyLogoErrorElement) this.companyLogoErrorElement.style.display = 'none';
        }

        // Restore original element references
        this.companyNameElement = this.savedCompanyNameElement;
        this.companyLogoElement = this.savedCompanyLogoElement;
        this.companyLogoErrorElement = this.savedCompanyLogoErrorElement;
        this.savedCompanyNameElement = null;
        this.savedCompanyLogoElement = null;
        this.savedCompanyLogoErrorElement = null;

        // Restore card-header company info and re-trigger symbol to redraw logo
        if (this.headerContainer) this.headerContainer.style.display = '';
        if (this.symbol && this.companyLogoElement) this.updateSymbol(this.symbol);
    }

    private chartHeight: number | null = null;
    private chartTop: number | null = null;
    private xAxisHeight: number | null = null;
    private yAxisHeight: number | null = null;
    private setChartPosition(height: number) {
        // Keep the raw height for cache comparison so panel-collapse/expand changes are
        // detected even in banner mode (where the adjusted height is always 0).
        const rawHeight = height;

        const isBannerActive = this.getMarketingAISignal()?.isBannerActive ?? false;

        // Banner just closed: revert element alignment and force cache miss so chart resizes correctly.
        if (this.bannerLayoutApplied && !isBannerActive) {
            this.teardownBannerAlignedLayout();
            this.chartHeight = null;
        }

        // In AI signal banner mode the stock-info panel is a flex sibling of .chart-container,
        // so .chart-container already has the correct height. Subtracting the panel height again
        // would leave empty space equal to the panel height.
        if (isBannerActive) {
            height = 0;
        }

        const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        const xAxis = this.base.tiChartSeries?.highStockChart?.xAxis?.[0];
        let top = 58;

        if (this.base.isExtraSmall()) {
            top = 4;
        } else if (this.base.element.containClass('one')) {
            top = 36;
        }

        if (!xAxis || !yAxis || (this.chartHeight == rawHeight && this.xAxisHeight == xAxis.height && this.yAxisHeight == yAxis.height && this.chartTop == top)) {
            return;
        }

        const chart = this.base.tiChartSeries?.chartElement;
        if (chart) {

            const chartService = (this.base.chartService as WebChartDataService);
            const topChange = this.base.tiChartSeries?.highStockChart && this.base.tiChartSeries?.highStockChart.spacing?.[0] != top;
            if (topChange) {
                chartService.disableAnnotations();
            }

            this.xAxisHeight = xAxis.height;
            this.yAxisHeight = yAxis.height;
            this.chartHeight = rawHeight;  // cache raw height so future changes are detected
            this.chartTop = top;
            chart.style.height = 'calc(100% - ' + height + 'px)';
            this.base.tiChartSeries?.highStockChart?.update({
                chart: {
                    spacingTop: top
                }
            }, false);

            this.base.tiChartSeries?.highStockChart?.redraw();
            this.base.tiChartSeries?.highStockChart?.reflow();

            if (topChange && !chartService.requestingInitData) {
                chartService.enableAnnotations(100, true);
                chartService.enableExitOrderAnnotations(100);
            }

            setTimeout(() => {
                this.setHistoricalButton();
            }, 150);
        }
    }

    private historicalButton: HistoricalChartButtonComponent | null = null;
    private setHistoricalButton() {
        if (!this.historicalButton) {
            this.historicalButton = this.base.getComponentsByType(HistoricalChartButtonComponent)[0] as HistoricalChartButtonComponent;
        }

        if (this.historicalButton) {
            this.historicalButton.setPosition();
        }
    }

    private expandClickTimer: any;
    private onExpandClick() {
        this.expandBtn?.querySelector('svg')?.addEventListener('click', () => {
            if (this.expandBtn.classList.contains('active')) {
                const elementHeight = this.element.offsetHeight;
                const elementMinHeight = this.iconWrapper?.offsetHeight;

                this.setChartPosition(0);

                if (this.element.style.height === 'auto') {
                    this.element.style.height = elementHeight + 'px';

                    setTimeout(() => {
                        this.expandBtn.classList.remove('active');
                        this.element.classList.remove('active');
                        this.element.style.height = elementMinHeight + 'px';
                    }, 150);
                } else {
                    this.expandBtn.classList.remove('active');
                    this.element.classList.remove('active');
                    this.element.style.height = elementMinHeight + 'px';
                }

                this.chartStyle && this.base.chartService.requestSaveSingleStockWindowState('true');
            } else {
                this.expandBtn.classList.add('active');
                this.element.classList.add('active');
                this.element.style.height = this.element.scrollHeight + 'px';

                this.chartStyle && this.base.chartService.requestSaveSingleStockWindowState('');
            }

            this.base.domEvents.triggerCustomEvent(CustomEventTypes.SingleStockWindowExpandCollapseStart);

            clearTimeout(this.expandClickTimer);
            this.expandClickTimer = setTimeout(() => {
                this.setContentSize();
                this.base.domEvents.triggerCustomEvent(CustomEventTypes.SingleStockWindowExpandCollapseDone);
            }, 500)
        });
    }

    public subscribeChartDataService() {
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
            this.setWindowState();
            this.setInputBackground();
        });
        this.base.chartService.subscribe_CompanyProfileData((data: CompanyProfileDataModel) => {
            this.setCompanyCountry(data?.country ?? '');
        });
        this.base.chartService.subscribeInitChartData((data: Array<TIChartPointModel>, timeframe: Timeframe, symbol: string) => {
            this.base.element.classList.add('single-stock-mode');

            this.coloredLastPrice?.reset();
            this.symbol = symbol;
            this.requestingSymbol = false;

            this.updateSymbol(symbol);

            setTimeout(() => {
                this.setContentSize();
                this.handleChangeFromClose();
            }, 500);
        });
        this.base.chartService.subscribeCurrentPositionViewModel((currentPositionData: any) => {
            this.setChartPosition(this.element.clientHeight);
            this.requestSetContentSize();
        });
        this.base.chartService.subscribe_SingleStockWindowData((singleStockData: SingleStockWindowDataModel) => {
            this.singleStockData = singleStockData;
            this.singleStockWindowChangeFromCloseManager?.updateSingleStockData(singleStockData);
            this.handleChangeFromClose();

            setTimeout(() => {
                this.updateSingleStockWindow();
                this.swapToShortNameIfTextOverflow();
            }, 100);
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (this.symbol === symbol) return;

            this.setCompanyCountry('');

            this.coloredLastPrice?.reset();

            if (this.changeFromCloseFirstBarElement) {
                this.changeFromCloseFirstBarElement.innerHTML = '';
            }

            if (this.changeFromCloseElement) {
                this.changeFromCloseElement.innerHTML = '';
            }

            this.updateSymbol(symbol);
        });
    }

    private requestSetContentSize() {
        setTimeout(() => {
            this.setContentSize();
            setTimeout(() => {
                this.setContentSize();
            }, 1200)
        }, 500);
    }

    private updateSymbol(symbol: string) {
        const imageUrl = this.symbolLogoUrl.replace('{0}', symbol);
        if (this.companyLogoElement && imageUrl != this.companyLogoElement.getAttribute('src')) {
            this.companyLogoElement.style.display = 'none';
            this.companyLogoElement.setAttribute('src', imageUrl);
        }

        if (this.symbolSwitcher && (!this.symbolSwitcher.value || this.symbolSwitcher.value.toLowerCase() != symbol.toLowerCase())) {
            if (this.symbolSwitcher) {
                this.symbolSwitcher.value = symbol;
            }
        }
    }

    private setWindowState() {
        if (!this.element.classList.contains('visually-hidden')) {
            return;
        }

        this.element.classList.remove('visually-hidden');
        this.expandBtn?.classList.remove('visually-hidden');

        if (this.chartStyle.singleStockNoBackground) {
            this.element.classList.add('no-bg');
        }

        if (this.chartStyle.totalVolumeDisplayTopRight) {
            this.element.findFirst('.volumes')?.addClass('two');
        }

        setTimeout(() => {
            this.element.style.opacity = '';
            this.element.style.visibility = '';

            const isValueExist = this.chartStyle.singleStockState;
            if (isValueExist) {
                this.element.classList.remove('active');
                this.expandBtn?.classList.remove('active');

                const elementMinHeight = this.iconWrapper?.offsetHeight + 5;
                this.element.style.height = elementMinHeight + 'px';
            } else {
                this.element.classList.add('active');
                this.expandBtn?.classList.add('active');

                this.element.style.height = 'auto';
            }

            this.setContentSize();
        }, 300);
    }

    private setCompanyCountry(country: string) {
        if (country) {
            const countryName = country.split(' ').join('-').split('.').join('');
            this.countryFlagElement?.setAttribute('src', this.symbolCountryUrl.replace('{0}', countryName));
            this.countryFlagElement?.setAttribute('title', countryName);
        } else {
            this.countryFlagElement?.classList.add('hidden');
        }
    }

    private onCountryImgLoaded() {
        this.countryFlagElement.onload = (e: Event) => {
            this.countryFlagElement?.classList.remove('hidden');
        };
        this.countryFlagElement.onerror = () => {
            this.countryFlagElement?.classList.add('hidden');
        };
    }

    private setInputBackground() {
        if (!this.chartStyle) { return; }

        let bgColor = '';
        const percent = this.chartStyle.singleStockBGPercent;
        if (this.chartStyle.singleStockInfoBackgroundColor) {
            bgColor = this.chartStyle.singleStockInfoBackgroundColor.toRGB();
        } else {
            if (this.chartStyle.isDarkTheme) {
                bgColor = this.chartStyle.singleStockBaseColor?.toLowerCase() == 'gradient' ? this.chartStyle.selectedChartGradientColor.lighten(percent) : this.chartStyle.selectedChartBackgroundColor.lighten(percent);
            } else {
                bgColor = this.chartStyle.singleStockBaseColor?.toLowerCase() == 'gradient' ? this.chartStyle.selectedChartGradientColor.darken(percent) : this.chartStyle.selectedChartBackgroundColor.darken(percent);
            }
        }

        if (this.chartStyle.isDarkTheme) {
            this.seperatorColor = this.chartStyle.singleStockSeparatorDarkColor;
        } else {
            this.seperatorColor = this.chartStyle.singleStockSeparatorLightColor;
        }

        if (this.stockSectionElement) {
            this.stockSectionElement.style.backgroundColor = bgColor;
            if (this.seperatorColor) {
                this.stockSectionElement.querySelectorAll<HTMLElement>('.sep').forEach((el: HTMLElement) => {
                    el.style.borderColor = this.seperatorColor;
                });
            }
        }

        if (this.symbolSwitcher) {
            if (this.extraSmallMode) {
                this.symbolSwitcher.style.backgroundColor = 'transparent';
            } else {
                this.symbolSwitcher.style.backgroundColor = bgColor;
            }
        }
    }

    private updateSingleStockWindow() {
        if (!this.singleStockData) {
            return;
        }

        const widowWidth = this.base.element.clientWidth;
        const isSmallestMode = widowWidth <= 460;
        const singleStockItems = this.singleStockData.getSingleStockItems(isSmallestMode, this.chartStyle?.totalVolumeDisplayTopRight ?? false);
        if (singleStockItems.length) {
            let renderedItems = 0;
            let requiredItems = 15;

            if (!this.stockSectionElement) {
                return;
            }

            if (this.stockSectionElement.classList.contains('four')) {
                requiredItems = 12;
            } else if (this.stockSectionElement.classList.contains('three')) {
                requiredItems = 9;
            } else if (this.stockSectionElement.classList.contains('two')) {
                requiredItems = 6;
            }

            const allColumns = Array.from(this.stockSectionElement.querySelectorAll<HTMLElement>('.col-auto') ?? []);
            allColumns.forEach((el: HTMLElement) => {
                el.classList.add('hidden');
            });


            for (let i = 0; i < singleStockItems.length; i++) {
                if (SingleStockWindowDataModel.doesNotHaveValueOrZero(singleStockItems[i].value)) {
                    continue;
                }

                const highlight = singleStockItems[i].cssClass;
                if (allColumns[renderedItems]) {
                    allColumns[renderedItems].className = `col-auto ${highlight}`;

                    const right = allColumns[renderedItems].querySelector('.right');
                    right && (right.innerHTML = singleStockItems[i].displayValue);
                    allColumns[renderedItems].querySelector('.sep')?.setAttribute('style', this.seperatorColor ? `border-color: ${this.seperatorColor}` : '');
                    if (allColumns[renderedItems].getAttribute('data-id') != singleStockItems[i].id + '') {
                        allColumns[renderedItems].setAttribute('data-id', singleStockItems[i].id + '');

                        const span = allColumns[renderedItems].querySelector('span');
                        const spanHidden = allColumns[renderedItems].querySelector('span.visually-hidden');

                        if (span) {
                            span.innerHTML = singleStockItems[i].name;
                        }

                        if (spanHidden) {
                            spanHidden.innerHTML = singleStockItems[i].name;
                        }
                    }

                    allColumns[renderedItems].classList.remove('hidden');
                }

                renderedItems++;
                if (renderedItems == requiredItems) {
                    break;
                }
            }
        } else {
            this.stockSectionElement?.querySelectorAll<HTMLElement>('.col-auto')?.forEach((el: HTMLElement) => {
                const right = el.querySelector('.right');
                if (right) {
                    right.innerHTML = '';
                }
            });
        }

        if (this.volElement) {
            const html = `
                <span>
                    <span>${SingleStockWindowDataModel.doesNotHaveValue(this.singleStockData.totalVolume) ? '' : FormatHelpers.numberToString(this.singleStockData.totalVolume)}</span>
                </span>`;
            this.volElement.innerHTML = html;
        }

        if (this.relVolElement) {
            const cssClass = (this.singleStockData.relativeVolume > 3 ? 'highlight2' : (this.singleStockData.relativeVolume > 1 ? 'highlight' : ''));
            const html = `
                <span class="${cssClass}">
                    <span>${SingleStockWindowDataModel.doesNotHaveValue(this.singleStockData.relativeVolume) ? '' : FormatHelpers.numberToStringThousandSeparators(this.singleStockData.relativeVolume, true, this.singleStockData.relativeVolume > 1000 ? 0 : 2)}</span>
                </span>`;

            this.relVolElement.innerHTML = html;
        }

        this.handleChangeFromClose();

        if (this.getMarketingAISignal()?.isBannerActive) {
            this.setupBannerAlignedLayout();
        }

        if (this.singleStockData.companyName) {
            if (this.companyNameElement) {
                this.companyNameElement.innerHTML = `${this.singleStockData.companyName}`;
            }
        } else {
            if (this.companyNameElement) {
                this.companyNameElement.innerHTML = `&nbsp;`;
            }
        }
    }

    private handleChangeFromClose() {
        if (!this.base.tiChartSeries.hasAnyDataPoints()) {
            this.changeFromCloseElement?.setInnerHtml('');
            return;
        }

        if (this.chartStyle?.changeFromCloseUseTradeQuote) {
            this.renderChangeFromCloseFirstBar({
                changesFromCloseDollar: this.singleStockData?.changesFromCloseDollar,
                changesFromClosePercentage: this.singleStockData?.changesFromClosePercentage,
            });
             this.renderChangeFromCloseElement({
                changesFromCloseDollar: this.singleStockData?.changesFromCloseDollar,
                changesFromClosePercentage: this.singleStockData?.changesFromClosePercentage,
            });
        } else {
            if (!this.singleStockWindowChangeFromCloseManager) {
                return;
            }

            const now = new Date();
            if (this.singleStockWindowChangeFromCloseManager.getShouldUseTopListForChangeFromClose(now)) {
                this.singleStockWindowChangeFromCloseManager.getChangeFromCloseValues().then((changesValues) => {
                    this.renderChangeFromCloseFirstBar(changesValues);
                    this.renderChangeFromCloseElement(changesValues);
                });
            } else if (SingleStockWindowDataModel.hasValue(this.singleStockData?.changesFromCloseDollar) && SingleStockWindowDataModel.hasValue(this.singleStockData?.changesFromClosePercentage)) {
                this.renderChangeFromCloseFirstBar({
                    changesFromCloseDollar: this.singleStockData?.changesFromCloseDollar,
                    changesFromClosePercentage: this.singleStockData?.changesFromClosePercentage,
                });
                this.renderChangeFromCloseElement({
                    changesFromCloseDollar: this.singleStockData?.changesFromCloseDollar,
                    changesFromClosePercentage: this.singleStockData?.changesFromClosePercentage,
                });
            }
        }
    }

    private buildChangeFromCloseText(dollar: number, percent: number): string {
        const prefix = dollar > 0 ? '+' : dollar < 0 ? '-' : '';
        const percentPrefix = dollar < 0 ? '-' : '';
        return `${prefix}${FormatHelpers.numberToString(Math.abs(dollar))} (${percentPrefix}${FormatHelpers.numberToString(Math.abs(percent))}%)`;
    }

    private renderChangeFromCloseFirstBar(changesValues: ChangeFromCloseValueResult) {
        if (!changesValues) { this.changeFromCloseFirstBarElement?.setInnerHtml(''); return; }

        const dollar = changesValues.changesFromCloseDollar;
        const percent = changesValues.changesFromClosePercentage;

        const lastPriceBgColor = this.chartStyle.getLastPriceUpColor() || '#32cd32';
        const lastPrice = this.coloredLastPrice?.getLastPriceToString() ?? '';

        const colorHex = dollar > 0
            ? (this.chartStyle.isDarkTheme ? `#00ff00` : `#269C26`)
            : dollar < 0 ? `rgb(255, 0, 0)` : '';

        const valueStyle = colorHex ? ` style="color: ${colorHex};"` : '';
        const html = `<span class="date"></span> `
            + `<span class="small-last-price" style="color: ${lastPriceBgColor};">${lastPrice}</span> `
            + `<span class="value"${valueStyle}>${this.buildChangeFromCloseText(dollar, percent)}</span>`;

        this.changeFromCloseFirstBarElement?.setInnerHtml(html);
    }

    private renderChangeFromCloseElement(changesValues: ChangeFromCloseValueResult) {
        let color = '';
        let text = '';

        if (changesValues) {
            const dollar = changesValues.changesFromCloseDollar;
            const percent = changesValues.changesFromClosePercentage;

            const upColor = this.chartStyle?.getChangeFromCloseUpColor() ?? '';
            const downColor = this.chartStyle?.getChangeFromCloseDownColor() ?? '';

            color = dollar < 0 ? (downColor || this.defaultDownColor) : (upColor || this.defaultUpColor);
            text = this.buildChangeFromCloseText(dollar, percent);
        }

        if (this.changeFromCloseElement) {
            this.changeFromCloseElement.style.color = color;
            this.changeFromCloseElement.innerHTML = text;
        }
    }

    private swapToShortNameIfTextOverflow() {
        if (!this.singleStockData) {
            return;
        }

        const widowWidth = this.base.element.clientWidth;
        const isSmallestMode = widowWidth <= 460;
        const singleStockItems = this.singleStockData.getSingleStockItems(isSmallestMode, this.chartStyle?.totalVolumeDisplayTopRight ?? false);
        this.stockSectionElement?.querySelectorAll<HTMLElement>('.col-auto')?.forEach((el: HTMLElement) => {
            const sswItem = singleStockItems.find(x => x.id == parseInt(el.getAttribute('data-id')));
            const span = el.querySelector('span');
            const spanVisually = el.querySelector('span.visually-hidden');
            const spanValue = el.querySelector('span.right');
            if (sswItem && span) {
                const style = window.getComputedStyle(el);
                const paddingRight = parseFloat(style.paddingRight.replace('px', ''));
                let name = '';
                if (spanVisually.clientWidth + spanValue.clientWidth + paddingRight + 5 > el.clientWidth) {
                    name = sswItem.shortName;
                } else {
                    name = sswItem.name;
                }

                if (span) {
                    span.innerHTML = name;
                }
            }
        });
    }

    private onImageLoaded() {
        if (!this.companyLogoElement || !this.companyLogoErrorElement) {
            return;
        }

        this.companyLogoElement.addEventListener('load', () => {
            TraceLoggingHelpers.log('image loaded')

            this.companyLogoElement.style.display = 'block';
            this.companyLogoErrorElement.style.display = 'none';
        });
        this.companyLogoElement.addEventListener('error', () => {
            TraceLoggingHelpers.log('image loaded error');
            let bgColor = '';
            if (myCommon) {
                bgColor = myCommon.colorFromSymbol(this.symbol);
            }

            if (bgColor) {
                this.companyLogoErrorElement.style.backgroundColor = bgColor;
            }

            this.companyLogoElement.style.display = 'none';
            this.companyLogoErrorElement.style.display = 'block';
        });
    }

    private checkIfJQueryExist(retriesCount: number) {
        if (retriesCount >= 100) {
            return;
        }

        if (typeof $ === 'undefined') {
            setTimeout(() => {
                this.checkIfJQueryExist(retriesCount++);
            }, 500);
        } else {
            require('../../node_modules/spectrum-colorpicker2');

            this.symbolSwitcher.classList.remove('visually-hidden');
            this.onDocumentReady();
        }
    }

    private onChartLoaded() {
        const ctx = this;
        const chartService = this.base.chartService as WebChartDataService;
        ctx.focusOnTextbox = chartService && chartService.symbolLookupFocusOnLoadQuery && !this.extraSmallMode;
        this.base.tiChartSeries.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                if (ctx.focusOnTextbox) {
                    ctx.focusOnTextbox = false;
                    if (ctx.chartSymbolLookup) {
                        ctx.chartSymbolLookup.setSymbolFocus();
                    } else {
                        $(ctx.symbolSwitcher).select();
                        $(ctx.symbolSwitcher).focus();
                    }
                }
            }, 500);
        });
    }

    private onDocumentReady() {
        const ctx = this;
        const chartService = this.base.chartService as WebChartDataService;
        $(document).ready(function () {
            if (chartService.symbollookup && chartService.symbollookup.toLowerCase() === 'jqueryui') {
                $.ajax({
                    type: "GET",
                    url: "/ajax/symbol.php",
                    success: function () {
                        const input = ctx.base.element.querySelector('.chart-symbol');
                        let id = '';
                        if (input) {
                            input.id = "chart-symbol-lookup-" + ctx.base.id;
                            id = "chart-symbol-lookup-" + ctx.base.id;
                        }
                        setTimeout(() => {
                            ctx.chartSymbolLookup = new symbolLookup(id, false, "chartSymbolLookup", (symbol: any) => {
                                ctx.requestSymbol(ctx.symbolSwitcher.value);
                                // ctx.chartSymbolLookup.setSymbolFocus();
                                // ctx.focusOnTextbox = true;
                            });
                        }, 300);
                    },
                    error: function () {
                        $(ctx.symbolSwitcher).keyup(function (e) {
                            if (e.keyCode == 13) {
                                ctx.requestSymbol(ctx.symbolSwitcher.value);
                            }
                        });
                    }
                });
            } else {
                $(ctx.symbolSwitcher).focus(function () {
                    $(ctx.symbolSwitcher).select();
                });
                $(ctx.symbolSwitcher).blur(function () {
                    if (!ctx.requestingSymbol) {
                        ctx.symbolSwitcher && (ctx.symbolSwitcher.value = ctx.symbol);
                    }
                });
                $(ctx.symbolSwitcher).keyup(function (e) {
                    if (e.keyCode == 13) {
                        ctx.requestSymbol(ctx.symbolSwitcher.value);
                    }
                });
                $.ajax({
                    type: "GET",
                    url: "/ajax/symbol.php",
                    success: function () {
                        $(ctx.symbolSwitcher).autocomplete('/ajax/symbol.php', {
                            width: 400,
                            selectFirst: true,
                            cacheLength: 0,
                            formatResult: function (row) {
                                var record = row.toString().replace(/^([^\s]+)\s.*$/, "$1");
                                return record;
                            },
                        });
                        $(ctx.symbolSwitcher).result(function () {
                            TraceLoggingHelpers.log('selected symbol ', ctx.symbolSwitcher.value);
                            ctx.requestSymbol(ctx.symbolSwitcher.value);
                        });
                    },
                    error: function () { }
                });
            }
        });
    }

    private requestSymbol(symbol: string) {
        if (!symbol) { return; }

        symbol = symbol.toUpperCase();

        this.requestingSymbol = true;
        this.coloredLastPrice?.reset();

        const chartContainer = this.base as any;
        chartContainer.chartService.requestNewSymbol(symbol);
        chartContainer.pnpContainer?.chartService?.requestNewSymbol(symbol);
    }

    private resizeTimer: any;
    private onElementResize() {
        this.base.domEvents.onResize(this.id, () => {
            this.setContentSize();

            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.setContentSize();
                this.swapToShortNameIfTextOverflow();
            }, 500);
        });
    }
}